import { Box, Flex, Text } from '@chakra-ui/react';
import { Dialog } from '@chakra-ui/react';
import { Skeleton } from '@/components/ui/Skeleton';
import { type ReactNode } from 'react';

/** Turn raw LLM text into formatted React nodes — handles **bold** and line breaks */
function formatExplanation(text: string): ReactNode {
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, i) => (
    <Box key={i} mb={i < paragraphs.length - 1 ? '2' : '0'}>
      {para.split('\n').map((line, j) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <Text key={j} as={j > 0 ? 'p' : 'span'} mt={j > 0 ? '1' : '0'}>
            {parts.map((part, k) =>
              part.startsWith('**') && part.endsWith('**') ? (
                <Text key={k} as="span" fontWeight="600" color="text.primary">
                  {part.slice(2, -2)}
                </Text>
              ) : (
                part
              ),
            )}
          </Text>
        );
      })}
    </Box>
  ));
}

interface ExplainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  value: string;
  category: string;
  explanation: string;
  loading: boolean;
}

export function ExplainDialog({ open, onOpenChange, label, value, category, explanation, loading }: ExplainDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Dialog.Backdrop
        bg="bg.overlay"
        position="fixed"
        inset="0"
        zIndex="100"
      />
      <Dialog.Positioner>
        <Dialog.Content
          bg="surface.primary"
          border="1px solid"
          borderColor="border.primary"
          borderRadius="lg"
          p="5"
          maxW="420px"
          w="90vw"
          position="relative"
        >
          <Dialog.Title
            fontSize="16px"
            fontWeight="600"
            color="text.primary"
            mb="1"
          >
            {label}
          </Dialog.Title>
          <Flex gap="2" align="center" mb="4">
            <Box
              as="span"
              bg="surface.secondary"
              px="8px"
              py="2px"
              borderRadius="sm"
              fontSize="13px"
              fontFamily="mono"
              color="text.secondary"
            >
              {value}
            </Box>
            <Text fontSize="12px" color="text.tertiary">{category}</Text>
          </Flex>

          <Box
            fontSize="14px"
            lineHeight="1.6"
            color="text.secondary"
            minH="60px"
          >
            {loading ? (
              <>
                <Skeleton width="100%" height="14px" />
                <Box h="8px" />
                <Skeleton width="90%" height="14px" />
                <Box h="8px" />
                <Skeleton width="70%" height="14px" />
              </>
            ) : (
              formatExplanation(explanation)
            )}
          </Box>

          <Dialog.CloseTrigger
            position="absolute"
            top="3"
            right="3"
            bg="transparent"
            border="none"
            color="text.tertiary"
            cursor="pointer"
            fontSize="18px"
            p="4px"
          >
            &times;
          </Dialog.CloseTrigger>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
